import {
  parseLatencyProbeMessage,
  type ClientStreamStatsMessage,
  type CongestionMessage,
  type MeasurementFreshness,
  type SourceCadenceReadContext,
  type SourceCadenceReader,
} from '@playcastdotio/PeerCore';
import { ClientStatsRouter } from '../ClientStatsRouter';
import { CongestionRecoveryManager, type CongestionRecoveryDeps } from '../CongestionRecoveryManager';
import type { LatencyProbeManager } from '../LatencyProbeManager';
import type { RecoveryHostCapacityStats } from '../RecoveryCapacityGate';
import type { PeerResolutionManager } from './PeerResolutionManager';

export interface SourceCadenceReaderFactory { create(): SourceCadenceReader }
export interface PeerRecoveryReportContext extends SourceCadenceReadContext { pollStartedAtMs: number }
export interface PeerRecoveryCompositionDependencies {
  recovery: CongestionRecoveryDeps;
  resolution: PeerResolutionManager;
  latency: LatencyProbeManager;
  sourceCadenceFactory: SourceCadenceReaderFactory;
  onClientStatsObserved?: (peerId: string, message: ClientStreamStatsMessage, freshness: MeasurementFreshness) => void;
  onFrameDecodeHints?: (peerId: string, hints: NonNullable<ClientStreamStatsMessage['frameDecodeHints']>) => void;
}

/** The application's recovery graph, shared with the isolated local browser utility. */
export class PeerRecoveryComposition {
  private readonly recovery: CongestionRecoveryManager;
  private readonly router: ClientStatsRouter;
  private readonly peers = new Set<string>();
  private readonly sources = new Map<string, { reader: SourceCadenceReader; captureGeneration: number }>();

  constructor(private readonly deps: PeerRecoveryCompositionDependencies) {
    this.recovery = new CongestionRecoveryManager({ ...deps.recovery,
      guestWallTimeBounds: (peerId, wallMs, epoch) => deps.latency.guestWallTimeBounds(peerId, wallMs, epoch),
      onPathChanged: (peerId, epoch) => {
        deps.resolution.observePath(peerId, epoch);
        deps.latency.resetForPath(peerId, epoch);
        deps.recovery.onPathChanged?.(peerId, epoch);
      } });
    this.router = new ClientStatsRouter({
      onClientStats: (peerId, message) => this.onClientStats(peerId, message),
      onLegacyCongestion: (peerId, message) => this.onCongestionMessage(peerId, message),
    });
  }

  public startPeer(peerId: string): void {
    this.peers.add(peerId);
    this.recovery.beginActivityWatch(peerId);
    this.deps.latency.start(peerId);
  }

  public onLocalReport(peerId: string, report: RTCStatsReport, context: PeerRecoveryReportContext,
    stats: RecoveryHostCapacityStats): void {
    this.peers.add(peerId);
    let source = this.sources.get(peerId);
    if (source && context.captureGeneration < source.captureGeneration) return;
    if (!source || source.captureGeneration !== context.captureGeneration) {
      source?.reader.reset();
      source = { reader: this.deps.sourceCadenceFactory.create(), captureGeneration: context.captureGeneration };
      this.sources.set(peerId, source);
    }
    const sourceCadence = source.reader.observe(report, context);
    this.observeSender(peerId, report, context);
    this.deps.resolution.tick(peerId);
    // Caller-supplied capacity must not smuggle in a fabricated source measurement.
    const { sourceCadence: _ignored, ...capacity } = stats;
    this.recovery.onLocalStats(peerId, { ...capacity, ...(sourceCadence ? { sourceCadence } : {}) });
  }

  public onDataMessage(peerId: string, message: string | ArrayBuffer): boolean {
    if (typeof message !== 'string') return false;
    let parsed: unknown;
    try { parsed = JSON.parse(message); } catch { return false; }
    return this.routeParsedMessage(peerId, parsed);
  }

  public routeParsedMessage(peerId: string, message: unknown): boolean {
    const latency = parseLatencyProbeMessage(message);
    if (latency) {
      this.peers.add(peerId);
      if (latency.role === 'pong') this.deps.latency.onPong(peerId, latency);
      else if (latency.role === 'report') this.deps.latency.onReport(peerId, latency);
      return true;
    }
    return this.router.route(peerId, message);
  }

  public onClientStats(peerId: string, message: ClientStreamStatsMessage): ReturnType<CongestionRecoveryManager['onClientStats']> {
    this.peers.add(peerId);
    const freshness = this.deps.resolution.observeReceiver(peerId, message);
    if (message.frameDecodeHints?.length) this.deps.onFrameDecodeHints?.(peerId, message.frameDecodeHints);
    this.deps.onClientStatsObserved?.(peerId, message, freshness);
    return this.recovery.onClientStats(peerId, message);
  }

  public getSnapshot(peerId: string): ReturnType<CongestionRecoveryManager['getSnapshot']> { return this.recovery.getSnapshot(peerId); }
  public getHandoffProtection(peerId: string): ReturnType<CongestionRecoveryManager['getHandoffProtection']> { return this.recovery.getHandoffProtection(peerId); }
  public getMobileQualitySnapshot(peerId: string): ReturnType<CongestionRecoveryManager['getMobileQualitySnapshot']> {
    this.peers.add(peerId);
    return this.recovery.getMobileQualitySnapshot(peerId);
  }
  public getPostClearReadiness(peerId: string): ReturnType<CongestionRecoveryManager['getPostClearReadiness']> { return this.recovery.getPostClearReadiness(peerId); }
  public primingEncodingFor(peerId: string): ReturnType<CongestionRecoveryManager['primingEncodingFor']> { return this.recovery.primingEncodingFor(peerId); }
  public restoreStickyFloorHold(peerId: string): boolean {
    this.peers.add(peerId);
    return this.recovery.restoreStickyFloorHold(peerId);
  }
  public onPlayerActivity(peerId: string): void { this.peers.add(peerId); this.recovery.onPlayerActivity(peerId); }
  public onCongestionMessage(peerId: string, message: CongestionMessage): void {
    this.peers.add(peerId);
    this.recovery.onCongestionMessage(peerId, message);
  }
  public clearStickySession(peerId: string): void {
    this.recovery.clearStickySession(peerId);
    this.deps.resolution.clearPeer(peerId, true);
  }

  public disposePeer(peerId: string): void {
    this.sources.get(peerId)?.reader.reset();
    this.sources.delete(peerId);
    this.router.clearPeer(peerId);
    this.deps.latency.stop(peerId);
    this.deps.resolution.clearPeer(peerId);
    this.recovery.clearPeer(peerId);
    this.peers.delete(peerId);
  }

  public dispose(): void {
    for (const peerId of this.peers) this.disposePeer(peerId);
    this.recovery.clearAll();
    this.deps.resolution.clearAll();
  }

  private observeSender(peerId: string, report: RTCStatsReport, context: PeerRecoveryReportContext): void {
    let outbound: RTCOutboundRtpStreamStats | undefined;
    let matches = 0;
    report.forEach(stat => {
      if (stat.id === context.outboundId && stat.type === 'outbound-rtp' && stat.kind === 'video') {
        outbound = stat as RTCOutboundRtpStreamStats;
        matches++;
      }
    });
    if (matches !== 1 || !outbound?.frameWidth || !outbound.frameHeight || outbound.framesEncoded === undefined) return;
    this.deps.resolution.observeSender(peerId, { width: outbound.frameWidth, height: outbound.frameHeight,
      frames: outbound.framesEncoded, streamId: `${outbound.id}:${outbound.ssrc ?? ''}`, pollStartedAtMs: context.pollStartedAtMs });
  }
}
